import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Printer,
} from "lucide-react";
import {
  Button,
  EmptyState,
  LoadingState,
  SectionCard,
  SelectInput,
  StatusBadge,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import SeletorCompetencia from "./SeletorCompetencia";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { abrirParaImprimir, dataCurta, quatroColunas } from "./pontoUtils";

const Resumo = ({ rotulo, valor, destaque = "" }) => (
  <div className="min-w-[92px]">
    <p className="text-[11px] uppercase tracking-wide text-gray-500">
      {rotulo}
    </p>
    <p
      className={`text-base font-semibold tabular-nums ${destaque || "text-gray-900"}`}
    >
      {valor}
    </p>
  </div>
);

const CorpoCartao = ({ cartao }) => (
  <Tabela className="border-t border-gray-200">
    <thead>
      <tr>
        <Th>Data</Th>
        <Th>Dia</Th>
        <Th className="text-center">Entrada</Th>
        <Th className="text-center">Saída</Th>
        <Th className="text-center">Entrada</Th>
        <Th className="text-center">Saída</Th>
        <Th className="text-center">Trabalhado</Th>
        <Th className="text-center">Atraso</Th>
        <Th className="text-center">H.E. 50%</Th>
        <Th className="text-center">H.E. 100%</Th>
        <Th className="text-center">A.N.</Th>
        <Th>Ocorrência</Th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100 bg-white">
      {cartao.dias.map((dia) => {
        const semExpediente = dia.feriado || dia.domingo;
        const alerta = dia.falta || dia.suspensao || dia.marcacaoIncompleta;
        return (
          <tr key={dia.data} className={semExpediente ? "bg-slate-50" : ""}>
            <Td className="tabular-nums">
              {dataCurta(dia.data)}
              {dia.possuiAjuste && (
                <span
                  className="text-blue-600"
                  title="Dia com marcação ajustada pela gestão"
                >
                  *
                </span>
              )}
            </Td>
            <Td>{dia.diaSemana}</Td>
            {quatroColunas(dia.marcacoes).map((horario, indice) => (
              <Td key={indice} className="text-center tabular-nums">
                {horario}
              </Td>
            ))}
            <Td className="text-center tabular-nums">{dia.trabalhado}</Td>
            <Td
              className={`text-center tabular-nums ${dia.atraso !== "-" ? "text-red-600" : ""}`}
            >
              {dia.atraso}
            </Td>
            <Td className="text-center tabular-nums">{dia.extra50}</Td>
            <Td className="text-center tabular-nums">{dia.extra100}</Td>
            <Td className="text-center tabular-nums">{dia.noturno}</Td>
            <Td
              className={alerta ? "font-medium text-red-600" : "text-gray-500"}
            >
              {dia.ocorrencia || ""}
              {dia.intervaloInsuficiente && (
                <span
                  className="ml-1 text-amber-700"
                  title="Intervalo menor que 1 hora em jornada acima de 6 horas"
                >
                  {dia.ocorrencia ? "· " : ""}intervalo curto
                </span>
              )}
            </Td>
          </tr>
        );
      })}
    </tbody>
  </Tabela>
);

/** Cartão ponto calculado (só a gestão vê) e impressão para assinatura/contabilidade. */
const CartoesTab = ({ colaboradoras, filtro, onFiltro, onAbrirAjustes }) => {
  const { showError, showInfo } = useNotification();
  const [cartoes, setCartoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [abertos, setAbertos] = useState({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await pontoService.listarCartoes(
        filtro.competencia,
        filtro.usuarioId,
      );
      setCartoes(lista);
      setAbertos(lista.length === 1 ? { [lista[0].usuarioId]: true } : {});
    } catch (erro) {
      setCartoes([]);
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [filtro.competencia, filtro.usuarioId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const imprimir = async (usuarioId) => {
    setImprimindo(true);
    try {
      const blob = await pontoService.gerarPdf(filtro.competencia, usuarioId);
      const nome = `cartao-ponto-${filtro.competencia}${usuarioId ? "" : "-todas"}.pdf`;
      if (!abrirParaImprimir(blob, nome)) {
        showInfo("O navegador bloqueou a nova aba; o PDF foi baixado.");
      }
    } catch (erro) {
      showError(erro.message);
    } finally {
      setImprimindo(false);
    }
  };

  const comPendencia = cartoes.filter(
    (cartao) => cartao.totais.diasMarcacaoIncompleta > 0,
  );

  return (
    <div className="space-y-4">
      <SectionCard bodyClassName="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SeletorCompetencia
            competencia={filtro.competencia}
            onChange={(competencia) => onFiltro({ ...filtro, competencia })}
          />
          <SelectInput
            aria-label="Colaboradora"
            className="!w-64"
            value={filtro.usuarioId}
            onChange={(evento) =>
              onFiltro({ ...filtro, usuarioId: evento.target.value })
            }
          >
            <option value="">Todas as colaboradoras</option>
            {colaboradoras.map((item) => (
              <option key={item.usuarioId} value={item.usuarioId}>
                {item.nome}
              </option>
            ))}
          </SelectInput>
        </div>
        <Button
          icon={Printer}
          loading={imprimindo}
          disabled={!cartoes.length}
          onClick={() => imprimir(filtro.usuarioId)}
        >
          {imprimindo
            ? "Gerando PDF..."
            : filtro.usuarioId
              ? "Imprimir cartão"
              : "Imprimir todos os cartões"}
        </Button>
      </SectionCard>

      {comPendencia.length > 0 && (
        <div className="flex flex-wrap items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 shrink-0" size={18} />
          <div className="flex-1">
            <p className="font-medium">
              Há dias com marcação incompleta (número ímpar de batidas).
            </p>
            <p>
              Corrija antes de imprimir: nesses dias não há cálculo de atraso
              nem de hora extra.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {comPendencia.map((cartao) => (
                <Button
                  key={cartao.usuarioId}
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onAbrirAjustes(cartao.usuarioId, filtro.competencia)
                  }
                >
                  Ajustar {cartao.nome.split(" ")[0]} (
                  {cartao.totais.diasMarcacaoIncompleta})
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {carregando && <LoadingState label="Calculando cartões..." />}

      {!carregando && cartoes.length === 0 && (
        <SectionCard>
          <EmptyState
            title="Nenhum cartão para mostrar."
            description="Cadastre a jornada das colaboradoras na aba Colaboradoras."
          />
        </SectionCard>
      )}

      {!carregando &&
        cartoes.map((cartao) => {
          const aberto = Boolean(abertos[cartao.usuarioId]);
          const { totais } = cartao;
          return (
            <SectionCard key={cartao.usuarioId} bodyClassName="p-0">
              <div className="flex flex-wrap items-center gap-4 px-4 py-3">
                <button
                  type="button"
                  className="flex min-w-[200px] flex-1 items-center gap-2 text-left"
                  aria-expanded={aberto}
                  onClick={() =>
                    setAbertos((atual) => ({
                      ...atual,
                      [cartao.usuarioId]: !aberto,
                    }))
                  }
                >
                  {aberto ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <span className="font-semibold text-gray-900">
                    {cartao.nome}
                  </span>
                  {totais.diasMarcacaoIncompleta > 0 && (
                    <StatusBadge variant="warning">
                      {totais.diasMarcacaoIncompleta} pendente(s)
                    </StatusBadge>
                  )}
                </button>
                <div className="flex flex-wrap gap-4">
                  <Resumo rotulo="Trabalhadas" valor={totais.trabalhado} />
                  <Resumo
                    rotulo="Atrasos"
                    valor={totais.atraso}
                    destaque={totais.atraso !== "-" ? "text-red-600" : ""}
                  />
                  <Resumo rotulo="H.E. 50%" valor={totais.extra50} />
                  <Resumo rotulo="H.E. 100%" valor={totais.extra100} />
                  <Resumo
                    rotulo="Faltas/Susp."
                    valor={totais.faltas + totais.suspensoes}
                    destaque={
                      totais.faltas + totais.suspensoes > 0
                        ? "text-red-600"
                        : ""
                    }
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Printer}
                  disabled={imprimindo}
                  onClick={() => imprimir(cartao.usuarioId)}
                >
                  Imprimir
                </Button>
              </div>
              {aberto && (
                <>
                  <CorpoCartao cartao={cartao} />
                  {Object.keys(totais.diasAfastamento || {}).length > 0 && (
                    <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-600">
                      {Object.entries(totais.diasAfastamento)
                        .map(([tipo, dias]) => `${tipo}: ${dias} dia(s)`)
                        .join(" · ")}
                    </p>
                  )}
                </>
              )}
            </SectionCard>
          );
        })}
    </div>
  );
};

export default CartoesTab;
